import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import "./terms.css";
import SEO from "../../components/SEO";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import { api } from "../../api/client";

const DEFAULT_TERMS_TITLE = "Short-let apartment house rules and regulations";
const DEFAULT_TERMS_LEDE =
  "These terms and conditions apply between you (Guest) and Luxistt. By using Luxistt’s website and services, you agree to be bound by these terms and all applicable laws. Luxistt may modify these terms at any time; please review them when you visit.";
const DEFAULT_SEO_TITLE = "Terms & House Rules";
const DEFAULT_SEO_DESCRIPTION =
  "Luxistt short-let apartment house rules and terms of service. Read before booking your stay.";

function TermsStaticSections() {
  return (
    <>
      <section className="terms-section">
        <h2 className="terms-section-title">Overall</h2>
        <p>
          Our short-let properties are furnished to a high standard. We may change layout, furniture or fittings and will update the website when possible. Differences between descriptions or photos and the actual property do not constitute grounds for cancellation or refunds. Internet is provided at no extra charge; we are not liable for losses from connection issues. Unless stated otherwise, quoted prices include utilities and housekeeping.
        </p>
        <p>
          <strong>Bookings and payments:</strong> A booking is confirmed only after you receive our confirmation. A pre-payment of 40% is due on booking; the balance must be paid 48 hours before check-in. Non-payment of the balance may result in cancellation with no refund. All bookings include 7.5% VAT on daily rates.
        </p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Cancellation and modification</h2>
        <p>
          Cancellation or modification must be made in writing to Luxistt. Non-payment of the balance results in automatic cancellation. <strong>Refund summary:</strong> cancel at least <strong>7 full calendar days before check-in</strong> and you receive a <strong>100% refund</strong> of eligible amounts paid. <strong>Any other refund is an 80% of total amount.</strong> Date or property changes are subject to availability. Cancellations cannot be made after check-in; payments are non-refundable after check-in.
        </p>
        <p>
          If we cancel a confirmed booking, we will try to find a suitable replacement or refund unused rental. Our liability is limited to the amount of unused rental. Check-in is from 2:00 pm and check-out by 12:00 pm; late departure may incur a full day’s charge. Furniture and effects must not be removed; you are liable for damage or loss. Our representatives may access the property for inspection or essential repairs.
        </p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Arrival and departure</h2>
        <p>
          The apartment is available from 2:00 pm on arrival and must be vacated by 12:00 pm on departure. Earlier arrival or later departure may be arranged with advance notice. Late exit may attract a 50% charge; exit after 4:00 pm incurs a full day fee. Please inform us of your arrival time in advance so we can welcome you and hand over keys.
        </p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Conduct of guests</h2>
        <p>
          Accommodation is for private residential use only. Maximum 5 guests. No parties or gatherings that disturb neighbours unless agreed in writing. Exceeding the guest limit may result in immediate termination and eviction without refund. Smoking inside the property is prohibited; evidence of smoking may result in a ₦50,000 cleaning fee.
        </p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Privacy policy</h2>
        <p>
          This policy describes how Luxistt collects, uses and discloses information from users of https://www.luxistt.com and applies to the site and all related products and services.
        </p>
        <h3 className="terms-subtitle">Personal identification information</h3>
        <p>
          We may collect personal information when you visit, register, book, respond to surveys or use other features. This may include name, email, phone and payment details. We collect such information only when you provide it voluntarily.
        </p>
        <h3 className="terms-subtitle">How we use collected information</h3>
        <p>We may use your information to: improve customer service; personalise your experience; improve our site; process bookings and send related updates. We do not share booking information with third parties except as needed to provide the service. You may opt in to marketing emails and unsubscribe at any time.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Payment</h2>
        <p>All payment is due before arrival. No cash payment on premises. Bed linen, bathroom and kitchen towels are included.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Non-smoking</h2>
        <p>The apartment and associated buildings are non-smoking. A designated outdoor area may be available for smoking.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Cleanliness</h2>
        <p>Please empty the refrigerator before leaving. Do not dispose of food waste, fats or feminine hygiene products in toilets, sinks or shower trays to avoid blockages. Please take care of the walls.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Keys</h2>
        <p>Keep keys safe and do not give them to unauthorised persons. Lost or missing keys may result in a replacement charge.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Animals</h2>
        <p>Animals are not allowed.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Liability</h2>
        <p>Parents are responsible for their children and guests. We are not responsible for damage to or loss of your personal belongings.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Responsibilities</h2>
        <p>Please treat the apartment and its contents with care. Report accidents when they happen. We reserve the right to charge for breakages or lost items at replacement value.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Changes to this privacy policy</h2>
        <p>Luxistt may update this policy at any time. We will post changes on the site and update the date below. Please check this page periodically.</p>
      </section>

      <section className="terms-section">
        <h2 className="terms-section-title">Unauthorised usage</h2>
        <p>Please inform us of your intended use of the apartment. We reserve the right to take action against commercial use without prior agreement.</p>
        <p>By using this site you accept this policy. For questions, contact us at <a href="mailto:info@luxistt.com">info@luxistt.com</a>, use the <Link to="/contact-us">contact form</Link> on this website, or reach us via WhatsApp from the chat button on any page.</p>
      </section>

    </>
  );
}

const Terms = () => {
  const [cms, setCms] = useState(null);
  const [cmsReady, setCmsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get("/public/marketing/site-content", null);
        if (!cancelled) setCms(data && typeof data === "object" ? data : null);
      } catch {
        if (!cancelled) setCms(null);
      } finally {
        if (!cancelled) setCmsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cmsBody =
    cmsReady &&
    cms &&
    typeof cms.terms_body_html === "string" &&
    cms.terms_body_html.trim() !== "";
  const pageTitle =
    (cms?.terms_page_title && String(cms.terms_page_title).trim()) ||
    DEFAULT_TERMS_TITLE;
  const pageLede =
    cms?.terms_page_lede != null && String(cms.terms_page_lede).trim() !== ""
      ? String(cms.terms_page_lede).trim()
      : DEFAULT_TERMS_LEDE;
  const seoTitle =
    cms?.terms_meta_title != null && String(cms.terms_meta_title).trim() !== ""
      ? String(cms.terms_meta_title).trim()
      : DEFAULT_SEO_TITLE;
  const seoDescription =
    cms?.terms_meta_description != null &&
    String(cms.terms_meta_description).trim() !== ""
      ? String(cms.terms_meta_description).trim()
      : DEFAULT_SEO_DESCRIPTION;

  return (
    <div className="terms-page">
      <SEO title={seoTitle} description={seoDescription} path="/terms" />
      <Navbar />
      <main className="terms-main">
        <div className="terms-content">
          <header className="terms-header">
            <h1 className="terms-title">{pageTitle}</h1>
            <p className="terms-lede">{pageLede}</p>
          </header>

          {cmsBody ? (
            <>
              <div
                className="terms-body-cms terms-section"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(String(cms.terms_body_html)),
                }}
              />
            </>
          ) : (
            <TermsStaticSections />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
